import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

const CurrencyContext = createContext(null);

const USD_URL = "https://ve.dolarapi.com/v1/dolares/oficial";
const EUR_URL = "https://ve.dolarapi.com/v1/euros/oficial";

const getRate = (data) => {
  const value = Number(data?.promedio);

  return Number.isFinite(value) && value > 0 ? value : 0;
};

const CurrencyProvider = ({ children }) => {
  const [rates, setRates] = useState({
    usd: 0,
    eur: 0,
    loading: true,
    error: null,
    updatedAt: null,
  });

  const fetchRates = useCallback(async () => {
    setRates((prev) => ({
      ...prev,
      loading: true,
      error: null,
    }));

    try {
      const [usdResponse, eurResponse] = await Promise.all([
        fetch(USD_URL, {
          cache: "no-store",
        }),
        fetch(EUR_URL, {
          cache: "no-store",
        }),
      ]);

      if (!usdResponse.ok || !eurResponse.ok) {
        throw new Error(
          `La API respondió ${usdResponse.status}/${eurResponse.status}`
        );
      }

      const [usdData, eurData] = await Promise.all([
        usdResponse.json(),
        eurResponse.json(),
      ]);

      const usd = getRate(usdData);
      const eur = getRate(eurData);

      if (!usd || !eur) {
        throw new Error(
          "La API no devolvió tasas válidas para USD/EUR."
        );
      }

      const usdUpdated = usdData?.fechaActualizacion || null;
      const eurUpdated = eurData?.fechaActualizacion || null;

      const updatedAt =
        [usdUpdated, eurUpdated]
          .filter(Boolean)
          .sort()
          .pop() || null;

      setRates({
        usd,
        eur,
        loading: false,
        error: null,
        updatedAt,
      });
    } catch (error) {
      console.error(
        "Error al obtener tasas oficiales:",
        error
      );

      setRates((prev) => ({
        ...prev,
        loading: false,
        error:
          error instanceof Error
            ? error.message
            : "No se pudieron obtener las tasas.",
      }));
    }
  }, []);

  useEffect(() => {
    fetchRates();
  }, [fetchRates]);

  return (
    <CurrencyContext.Provider
      value={{
        usd: rates.usd,
        eur: rates.eur,
        loading: rates.loading,
        error: rates.error,
        updatedAt: rates.updatedAt,
        refreshRates: fetchRates,
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
};

export { CurrencyProvider };

export const useCurrency = () => {
  const context = useContext(CurrencyContext);

  if (!context) {
    throw new Error(
      "useCurrency debe utilizarse dentro de <CurrencyProvider>."
    );
  }

  return context;
};

export default CurrencyProvider;